import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Loading, useAsync } from '../components/common';
import type { LambdaDetail, LambdaSummary, ParentSummary } from '../types';

type Cell = {
  enabled: boolean;
  status: 'on' | 'off' | 'missing' | 'n/a';
  fileTypes: string[];
  extra: Record<string, unknown>;
  webhook: boolean;
  bucket?: string;
  snowflake?: string | null;
};

export default function MatrixPage() {
  const parents = useAsync(() => api.listParents(), []);
  const lambdas = useAsync(() => api.listLambdas(), []);
  const [details, setDetails] = useState<Record<string, LambdaDetail>>({});
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<
    'partner_aware' | 'all' | 'required' | 'settlement_operations' | 'lead_scoring' | 'general'
  >('settlement_operations');
  const [statusFilter, setStatusFilter] = useState<'active_draft' | 'active' | 'all'>('active_draft');

  const visibleLambdas = useMemo(() => {
    const rows = lambdas.data ?? [];
    return rows.filter((l) => {
      if (filter === 'partner_aware' && !l.partner_aware) return false;
      if (filter === 'required' && !l.required_for_active_parent) return false;
      if (
        (filter === 'settlement_operations' ||
          filter === 'lead_scoring' ||
          filter === 'general') &&
        l.ops_area !== filter
      ) {
        return false;
      }
      return true;
    });
  }, [lambdas.data, filter]);

  const visibleParents = useMemo(() => {
    const rows = parents.data ?? [];
    return rows.filter((p) => {
      if (statusFilter === 'active') return p.status === 'active';
      if (statusFilter === 'active_draft') return p.status === 'active' || p.status === 'draft';
      return true;
    });
  }, [parents.data, statusFilter]);

  async function ensureLoaded(keys: string[]) {
    const missing = keys.filter((k) => !details[k] && !loadingKeys.includes(k));
    if (missing.length === 0) return;
    setLoadingKeys((prev) => [...prev, ...missing]);
    const loaded = await Promise.all(
      missing.map(async (key) => {
        try {
          return [key, await api.getLambda(key)] as const;
        } catch {
          return null;
        }
      }),
    );
    setDetails((prev) => {
      const next = { ...prev };
      for (const item of loaded) {
        if (item) next[item[0]] = item[1];
      }
      return next;
    });
    setLoadingKeys((prev) => prev.filter((k) => !missing.includes(k)));
  }

  useEffect(() => {
    if (!lambdas.data) return;
    void ensureLoaded(visibleLambdas.map((l) => l.lambda_key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lambdas.data, filter, visibleLambdas.map((l) => l.lambda_key).join(',')]);

  if (parents.loading || lambdas.loading || !parents.data || !lambdas.data) {
    return <Loading error={parents.error || lambdas.error} />;
  }

  function cellFor(lambda: LambdaSummary, parent: ParentSummary): Cell {
    if (!lambda.partner_aware) {
      return { enabled: false, status: 'n/a', fileTypes: [], extra: {}, webhook: false };
    }
    const detail = details[lambda.lambda_key];
    if (!detail) {
      return { enabled: false, status: 'missing', fileTypes: [], extra: {}, webhook: false };
    }
    const row = detail.bindings.find((b) => b.parent_key === parent.parent_key);
    if (!row?.binding) {
      return { enabled: false, status: 'missing', fileTypes: [], extra: {}, webhook: false };
    }
    const b = row.binding;
    return {
      enabled: b.enabled,
      status: b.enabled ? 'on' : 'off',
      fileTypes: b.file_type_keys,
      extra: b.extra || {},
      webhook: b.webhook_enabled,
      bucket: row.effective.bucket,
      snowflake: row.effective.snowflake_profile_key,
    };
  }

  const stillLoading = loadingKeys.length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Config matrix</h1>
          <p>
            Estate-wide view of which DSP parent is wired to which Lambda. Click a cell to open the
            function&apos;s binding editor — that is the configuration the runtime snapshot will
            publish.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ alignItems: 'center' }}>
          <label className="field" style={{ flex: '0 1 220px' }}>
            <span>Lambdas</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
              <option value="settlement_operations">Settlement Operations</option>
              <option value="lead_scoring">Lead Scoring</option>
              <option value="general">General Lambdas</option>
              <option value="partner_aware">All partner-aware</option>
              <option value="required">Required for active parents</option>
              <option value="all">All registered</option>
            </select>
          </label>
          <label className="field" style={{ flex: '0 1 220px' }}>
            <span>Parents</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="active_draft">Active + draft</option>
              <option value="active">Active only</option>
              <option value="all">All statuses</option>
            </select>
          </label>
          <div className="small muted">
            {stillLoading
              ? `Loading binding detail… (${loadingKeys.length} left)`
              : `${visibleLambdas.length} lambdas × ${visibleParents.length} parents`}
          </div>
        </div>

        <div className="legend chips" style={{ marginBottom: 12 }}>
          <Badge tone="ok">on</Badge>
          <Badge>off</Badge>
          <Badge tone="danger">missing</Badge>
          <Badge tone="info">n/a (not partner-aware)</Badge>
        </div>

        <div className="table-scroll matrix-scroll">
          <table className="matrix">
            <thead>
              <tr>
                <th className="sticky-col">Lambda</th>
                {visibleParents.map((p) => (
                  <th key={p.parent_key}>
                    <Link to={`/parents/${p.parent_key}`}>{p.display_name}</Link>
                    <div className="small muted mono">{p.parent_key}</div>
                    <Badge tone={p.status === 'active' ? 'ok' : 'warn'}>{p.status}</Badge>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleLambdas.map((lambda) => (
                <tr key={lambda.lambda_key}>
                  <td className="sticky-col">
                    <Link to={`/lambdas/${lambda.lambda_key}`}>{lambda.display_name}</Link>
                    <div className="small muted mono">{lambda.lambda_key}</div>
                    {lambda.required_for_active_parent && (
                      <Badge tone="danger">required</Badge>
                    )}
                  </td>
                  {visibleParents.map((parent) => {
                    const cell = cellFor(lambda, parent);
                    const tone =
                      cell.status === 'on'
                        ? 'ok'
                        : cell.status === 'missing'
                          ? 'danger'
                          : cell.status === 'n/a'
                            ? 'info'
                            : 'default';
                    const title = [
                      cell.status,
                      cell.fileTypes.length ? `files: ${cell.fileTypes.join(', ')}` : '',
                      cell.bucket ? `bucket: ${cell.bucket}` : '',
                      cell.snowflake ? `snowflake: ${cell.snowflake}` : '',
                      cell.webhook ? 'webhook: on' : '',
                      Object.keys(cell.extra).length
                        ? `extra: ${JSON.stringify(cell.extra)}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join('\n');
                    return (
                      <td key={parent.parent_key} title={title}>
                        <Link
                          to={`/lambdas/${lambda.lambda_key}`}
                          className={`matrix-cell ${cell.status}`}
                        >
                          <Badge tone={tone}>{cell.status}</Badge>
                          {cell.fileTypes.length > 0 && (
                            <div className="small mono">
                              {cell.fileTypes
                                .slice(0, 2)
                                .map((f) => f.replace(/_/g, '-'))
                                .join(', ')}
                              {cell.fileTypes.length > 2 ? '…' : ''}
                            </div>
                          )}
                          {Object.keys(cell.extra).length > 0 && (
                            <div className="small muted">extra</div>
                          )}
                        </Link>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
